import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";

import ProtectedRoute from "components/ProtectedRoute";
import PublicRoute from "components/PublicRoute";
import RootRedirect from "components/RootRedirect";
import PermissionRoute from "components/PermissionRoute";
import BrandedLoader from "components/ui/BrandedLoader";

const Dashboard = lazy(() => import("./pages/dashboard"));
const ConversationsListView = lazy(() => import("./pages/conversations-list-view"));
const WhatsAppChannelsPage = lazy(() => import("./pages/whatsapp-channels"));
const ServicesPage = lazy(() => import("./pages/services"));
const ProductWorkspace = lazy(() => import("./pages/product-workspaces"));
const ProductPageShell = lazy(() => import("./pages/product-workspaces/ProductPageShell"));
const WhatsAppSubscriptionPage = lazy(() => import("./pages/product-workspaces/WhatsAppSubscriptionPage"));
const WhatsAppCampaignsPage = lazy(() => import("./pages/product-workspaces/WhatsAppCampaignsPage"));
const WhatsAppAdsPage = lazy(() => import("./pages/product-workspaces/WhatsAppAdsPage"));
const WhatsAppAutomationsPage = lazy(() => import("./pages/product-workspaces/WhatsAppAutomationsPage"));
const WhatsAppDeveloperApiPage = lazy(() => import("./pages/product-workspaces/WhatsAppDeveloperApiPage"));
const WhatsAppFlowBuilderPage = lazy(() => import("./pages/product-workspaces/WhatsAppFlowBuilderPage"));
const EmailMarketingPage = lazy(() => import("./pages/product-workspaces/EmailMarketingPage"));
const OrganizationDomainsPage = lazy(() => import("./pages/product-workspaces/OrganizationDomainsPage"));
const AiChatSubscriptionPage = lazy(() => import("./pages/product-workspaces/AiChatSubscriptionPage"));
const ProductSubscriptionPage = lazy(() => import("./pages/product-workspaces/ProductSubscriptionPage"));
const JobsWorkspace = lazy(() => import("./pages/jobs-workspace"));
const MessagingAnalyticsOverview = lazy(() => import("./pages/messaging-analytics-overview"));
const Login = lazy(() => import("./pages/login"));
const VendorSignup = lazy(() => import("./pages/vendor/signup"));
const VerifyEmail = lazy(() => import("./pages/verify-email"));
const ForgotPassword = lazy(() => import("./pages/forgot-password"));
const ResetPassword = lazy(() => import("./pages/reset-password"));
const ProfileSettings = lazy(() => import("./pages/profile-settings"));
const VendorSettings = lazy(() => import("./pages/vendor/settings"));
const CrmConversionLinksPage = lazy(() => import("pages/crm-conversion-links"));
const CrmConversionSettingsPage = lazy(() => import("pages/crm-conversion-settings"));
const CrmCustomizationPage = lazy(() => import("pages/crm-customization"));
const AiChatSetupPage = lazy(() => import("pages/ai-chat-setup"));
const BillingPage = lazy(() => import("pages/billing"));
const LeadsPage = lazy(() => import("pages/leads"));
const ContactsPage = lazy(() => import("pages/contacts"));
const NotificationsPage = lazy(() => import("pages/notifications"));
const SupportTicketsPage = lazy(() => import("pages/support-tickets"));
const ActionQueuePage = lazy(() => import("pages/action-queue"));
const AiTrainingPage = lazy(() => import("pages/ai-training"));
const ActivityLogPage = lazy(() => import("pages/activity-log"));
const AccountSecurity = lazy(() => import("pages/account-security"));

const RouteLoader = () => (
  <BrandedLoader />
);

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />

        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            {/* -------- Public (Unauthenticated) Routes -------- */}
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<VendorSignup />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>

            {/* -------- Protected (Authenticated) Routes -------- */}
            <Route element={<ProtectedRoute />}>
              {/* CustomerGraph is paused; existing links lead to supported CRM/billing pages. */}
              <Route path="/products/customergraph/subscription" element={<Navigate to="/products/billing/subscriptions" replace />} />
              <Route path="/products/customergraph" element={<Navigate to="/products/crm/contacts" replace />} />
              <Route path="/products/customergraph/*" element={<Navigate to="/products/crm/contacts" replace />} />
              <Route path="/account-security" element={<AccountSecurity />} />
              <Route element={<PermissionRoute permission="VIEW_DASHBOARD" />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/support" element={<Navigate to="/products/support" replace />} />
                <Route
                  path="/products/support"
                  element={<ProductWorkspace productCodeOverride="support" />}
                />
                <Route
                  path="/products/support/tickets"
                  element={(
                    <ProductPageShell productCode="support">
                      <SupportTicketsPage embedded section="tickets" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="VIEW_DASHBOARD" />}>
                <Route
                  path="/products/organization"
                  element={<ProductWorkspace productCodeOverride="organization" />}
                />
                <Route
                  path="/products/organization/account"
                  element={(
                    <ProductPageShell productCode="organization">
                      <ProfileSettings embedded />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/organization/profile"
                  element={(
                    <ProductPageShell productCode="organization">
                      <VendorSettings embedded section="profile" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/organization/team"
                  element={(
                    <ProductPageShell productCode="organization">
                      <VendorSettings embedded section="team" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/organization/domains"
                  element={(
                    <ProductPageShell productCode="organization">
                      <OrganizationDomainsPage />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/organization/security"
                  element={<Navigate to="/products/organization/account" replace />}
                />
                <Route
                  path="/products/organization/activity"
                  element={(
                    <ProductPageShell productCode="organization">
                      <ActivityLogPage embedded tone="organization" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" />}>
                <Route
                  path="/products/billing/subscriptions"
                  element={(
                    <ProductPageShell productCode="billing">
                      <BillingPage embedded section="subscriptions" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/billing/invoices"
                  element={(
                    <ProductPageShell productCode="billing">
                      <BillingPage embedded section="invoices" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/billing/tax"
                  element={(
                    <ProductPageShell productCode="billing">
                      <BillingPage embedded section="tax" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_CHANNELS" productAccess="PLATFORM" />}>
                <Route path="/services" element={<ServicesPage />} />
              </Route>
              <Route element={<PermissionRoute permission="VIEW_DASHBOARD" />}>
                <Route path="/products/hosting/websites" element={<ProductWorkspace productCodeOverride="hosting" />} />
                <Route path="/products/hosting/dns" element={<ProductWorkspace productCodeOverride="hosting" />} />
                <Route path="/products/hosting/analytics" element={<ProductWorkspace productCodeOverride="hosting" />} />
                <Route path="/products/hrms/configuration" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/access" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/modules" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/branding" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/security" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/integrations" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/usage" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/employees" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/documents" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/attendance" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/leave" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/approvals" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/payroll" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/recruitment" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/onboarding" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/performance" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/learning" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/expenses" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/analytics" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/benefits" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/compliance" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route path="/products/hrms/*" element={<ProductWorkspace productCodeOverride="hrms" />} />
                <Route element={<PermissionRoute permission="MANAGE_CHANNELS" productAccess="JOBS" />}>
                  <Route path="/products/jobs" element={<ProductPageShell productCode="jobs"><JobsWorkspace section="overview" /></ProductPageShell>} />
                  <Route path="/products/jobs/openings" element={<ProductPageShell productCode="jobs"><JobsWorkspace section="openings" /></ProductPageShell>} />
                  <Route path="/products/jobs/candidates" element={<ProductPageShell productCode="jobs"><JobsWorkspace section="candidates" /></ProductPageShell>} />
                  <Route path="/products/jobs/usage" element={<ProductPageShell productCode="jobs"><JobsWorkspace section="usage" /></ProductPageShell>} />
                </Route>
                <Route path="/products/:productCode" element={<ProductWorkspace />} />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_CHANNELS" productAccess="WHATSAPP" />}>
                <Route
                  path="/products/whatsapp/numbers"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppChannelsPage embedded section="numbers" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/whatsapp/templates"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppChannelsPage embedded section="templates" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/whatsapp/automations"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppAutomationsPage />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/whatsapp/campaigns"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppCampaignsPage />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/whatsapp/ads"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppAdsPage />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/whatsapp/developer-api"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppDeveloperApiPage />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/whatsapp/docs"
                  element={<Navigate to="/products/whatsapp/developer-api" replace />}
                />
                <Route
                  path="/products/whatsapp/flows"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppFlowBuilderPage />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_CONVERSATIONS" productAccess="WHATSAPP" />}>
                <Route
                  path="/products/whatsapp/inbox"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <ConversationsListView embedded workspace="whatsapp" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/whatsapp/inbox/:conversationId"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <ConversationsListView embedded workspace="whatsapp" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="WHATSAPP" />}>
                <Route
                  path="/products/whatsapp/subscription"
                  element={(
                    <ProductPageShell productCode="whatsapp">
                      <WhatsAppSubscriptionPage />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="AI_CHAT" />}>
                <Route
                  path="/products/ai-chat/subscription"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <AiChatSubscriptionPage />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="CRM" />}>
                <Route
                  path="/products/crm/subscription"
                  element={(
                    <ProductPageShell productCode="crm">
                      <ProductSubscriptionPage productCode="CRM" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="ANALYTICS" />}>
                <Route
                  path="/products/analytics/subscription"
                  element={(
                    <ProductPageShell productCode="analytics">
                      <ProductSubscriptionPage productCode="ANALYTICS" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="EMAIL_MARKETING" />}>
                <Route
                  path="/products/email-marketing/subscription"
                  element={(
                    <ProductPageShell productCode="email-marketing">
                      <ProductSubscriptionPage productCode="EMAIL_MARKETING" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="HOSTING" />}>
                <Route
                  path="/products/hosting/subscription"
                  element={(
                    <ProductPageShell productCode="hosting">
                      <ProductSubscriptionPage productCode="HOSTING" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="HRMS" />}>
                <Route
                  path="/products/hrms/subscription"
                  element={(
                    <ProductPageShell productCode="hrms">
                      <ProductSubscriptionPage productCode="HRMS" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_BILLING" productAccess="JOBS" />}>
                <Route
                  path="/products/jobs/subscription"
                  element={(
                    <ProductPageShell productCode="jobs">
                      <ProductSubscriptionPage productCode="JOBS" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_CHANNELS" productAccess="EMAIL_MARKETING" />}>
                <Route
                  path="/products/email-marketing/campaigns"
                  element={(
                    <ProductPageShell productCode="email-marketing">
                      <EmailMarketingPage section="campaigns" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/email-marketing/templates"
                  element={(
                    <ProductPageShell productCode="email-marketing">
                      <EmailMarketingPage section="templates" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/email-marketing/subscribers"
                  element={(
                    <ProductPageShell productCode="email-marketing">
                      <EmailMarketingPage section="subscribers" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/email-marketing/sender"
                  element={(
                    <ProductPageShell productCode="email-marketing">
                      <EmailMarketingPage section="sender" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="TRAIN_AI" productAccess="AI_CHAT" />}>
                <Route
                  path="/products/ai-chat/agent"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <AiTrainingPage embedded section="agent" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/ai-chat/knowledge"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <AiTrainingPage embedded section="knowledge" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/ai-chat/preview"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <AiTrainingPage embedded section="preview" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/ai-chat/rules"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <AiTrainingPage embedded section="rules" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/ai-chat/setup"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <AiChatSetupPage embedded section="quick-setup" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/ai-chat/widget"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <AiChatSetupPage embedded section="widget" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_CONVERSATIONS" productAccess="AI_CHAT" />}>
                <Route
                  path="/products/ai-chat/inbox"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <ConversationsListView embedded workspace="ai" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/ai-chat/inbox/:conversationId"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <ConversationsListView embedded workspace="ai" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/ai-chat/actions"
                  element={(
                    <ProductPageShell productCode="ai-chat">
                      <ActionQueuePage embedded workspace="ai" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_LEADS" productAccess="CRM" />}>
                <Route
                  path="/products/crm/contacts"
                  element={(
                    <ProductPageShell productCode="crm">
                      <ContactsPage embedded />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/crm/contacts/:contactId"
                  element={(
                    <ProductPageShell productCode="crm">
                      <ContactsPage embedded />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/crm/memory"
                  element={(
                    <ProductPageShell productCode="crm">
                      <Navigate to="/products/crm/contacts" replace />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_CONVERSATIONS" productAccess="CRM" />}>
                <Route
                  path="/products/crm/timeline"
                  element={(
                    <ProductPageShell productCode="crm">
                      <ConversationsListView embedded workspace="crm" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/crm/timeline/:conversationId"
                  element={(
                    <ProductPageShell productCode="crm">
                      <ConversationsListView embedded workspace="crm" />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_LEADS" productAccess="CRM" />}>
                <Route
                  path="/products/crm/leads"
                  element={(
                    <ProductPageShell productCode="crm">
                      <LeadsPage embedded />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/crm/leads/:leadId"
                  element={(
                    <ProductPageShell productCode="crm">
                      <LeadsPage embedded />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/crm/follow-ups"
                  element={(
                    <ProductPageShell productCode="crm">
                      <ActionQueuePage embedded workspace="crm" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/crm/customization"
                  element={(
                    <ProductPageShell productCode="crm">
                      <CrmCustomizationPage embedded />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="MANAGE_CONVERSIONS" productAccess="CRM" />}>
                <Route
                  path="/products/crm/conversion-links"
                  element={(
                    <ProductPageShell productCode="crm">
                      <CrmConversionLinksPage embedded />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/crm/conversion-settings"
                  element={(
                    <ProductPageShell productCode="crm">
                      <CrmConversionSettingsPage embedded />
                    </ProductPageShell>
                  )}
                />
              </Route>
              <Route element={<PermissionRoute permission="VIEW_ANALYTICS" productAccess="ANALYTICS" />}>
                <Route
                  path="/products/analytics/messaging"
                  element={(
                    <ProductPageShell productCode="analytics">
                      <MessagingAnalyticsOverview embedded />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/analytics/conversion"
                  element={(
                    <ProductPageShell productCode="analytics">
                      <MessagingAnalyticsOverview embedded focus="conversion" />
                    </ProductPageShell>
                  )}
                />
                <Route
                  path="/products/analytics/activity"
                  element={(
                    <ProductPageShell productCode="analytics">
                      <ActivityLogPage embedded tone="analytics" />
                    </ProductPageShell>
                  )}
                />
              </Route>
            </Route>

            {/* -------- 404 -------- */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default AppRoutes;
